import {Breadcrumb, Button, Form, Input, message, Select} from "antd";
import {useForm} from "antd/es/form/Form";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {AppContextContext, OrganizationServiceContext, SbomProjectGroupServiceContext, SbomProjectServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import SbomProject from "../../domain/SbomProject";
import QueryOptions from "../../sal-ui/QueryOptions";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import {definedValues} from "../../utils/SelectOptionUtils";
import {matchesSearchText} from "../../utils/StringUtils";
import * as globalStyles from "../App.module.css";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";
import {DocumentTitle} from "../DocumentTitle";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function SbomProjectGroupAdd() {
    const appContext = useContext(AppContextContext);
    const sbomProjectGroupService = useContext(SbomProjectGroupServiceContext);
    const sbomProjectService = useContext(SbomProjectServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const navigate = useNavigate();
    const [form] = useForm();
    const selectedOrganizationId = Form.useWatch("organization", form);
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [availableProjects, setAvailableProjects] = useState<SbomProject[]>();
    const userIsSystemAdmin = appContext.user.isSystemAdmin();
    const userOrganizationId = appContext.user.organization?.id;
    const projectOrganizationId = userIsSystemAdmin ? selectedOrganizationId ?? userOrganizationId : undefined;

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 16},
    };

    const title = "Add new project group";

    useEffect(() => {
        if (!userIsSystemAdmin) {
            return;
        }

        organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(value => setOrganizations(value.data));
    }, [organizationService, userIsSystemAdmin]);

    useEffect(() => {
        if (userIsSystemAdmin && !projectOrganizationId) {
            setAvailableProjects(undefined);

            return;
        }

        sbomProjectService.getListForOrganizationScope(projectOrganizationId)
            .then(value => setAvailableProjects(value.data));
    }, [projectOrganizationId, sbomProjectService, userIsSystemAdmin]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb
                    items={[
                        {title: appContext.config?.appName},
                        {title: <Link to={"/sbom-project-groups"}>{"Project groups"}</Link>},
                        {title}
                    ]}
                />

                <h1>{title}</h1>

                <Form {...layout}
                      form={form}
                      className={`${globalStyles["common__form"]}`}
                      initialValues={userIsSystemAdmin ? {organization: userOrganizationId} : undefined}
                      onFinish={onFinish}>
                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."},
                            {
                                validator: serverViolationsHolder.createServerValidator(
                                    'UNIQUE',
                                    'name, organization_id',
                                    undefined,
                                    {compareLowerCaseValues: true}
                                ),
                                message: "Name is already used."
                            }
                        ]}>
                        <Input autoFocus={true} maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"description"}
                        label={"Description"}>
                        <Input.TextArea maxLength={1000} rows={4}/>
                    </Form.Item>

                    {userIsSystemAdmin &&
                        <Form.Item
                            name={"organization"}
                            label={"Organization"}
                            rules={[{required: true, message: "Organization is required."}]}>
                            <Select onChange={() => form.setFieldValue("projectIds", [])}>
                                {organizations?.map(organization =>
                                    <Select.Option key={organization.id} value={organization.id}>{organization.name}</Select.Option>
                                )}
                            </Select>
                        </Form.Item>
                    }

                    <Form.Item
                        name={"projectIds"}
                        label={"Projects"}>
                        <SelectAllMultiSelect
                            selectAllValues={definedValues(availableProjects, project => project.id)}
                            showSearch={true}
                            filterOption={(inputValue, option) => matchesSearchText(inputValue, option?.label)}
                            placeholder={"no projects"}>
                            {availableProjects?.map(project =>
                                <Select.Option key={project.id} value={project.id} label={project.name}>{project.name}</Select.Option>
                            )}
                        </SelectAllMultiSelect>
                    </Form.Item>

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>Save</Button>
                        <Button onClick={() => navigate("/sbom-project-groups")}>Cancel</Button>
                    </Form.Item>
                </Form>
            </>
        </DocumentTitle>
    );

    function onFinish(values: any) {
        sbomProjectGroupService.add(values)
            .then(
                (id) => {
                    message.success(<>Project group <b>{values.name}</b> successfully added.</>);

                    navigate(`/sbom-project-groups/${id}`);
                },
                error => serverViolationsHolder.handleServerError(error, form)
            );
    }
}

export default SbomProjectGroupAdd;
